import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { FileText, LayoutGrid, BarChart2, Share2 } from 'lucide-react';

// Define steps content outside component for reuse and SEO
const WORKFLOW_STEPS = [
  {
    id: 1,
    title: "Document Intelligence",
    description: "Extract data from documents, integrations, or the web",
    icon: <FileText className="w-6 h-6 text-blue-600" />,
    color: "blue",
    ariaLabel: "Document Intelligence Step"
  },
  {
    id: 2,
    title: "Workflows",
    description: "Run a series of data processing steps using a no-code workflow editor",
    icon: <LayoutGrid className="w-6 h-6 text-green-600" />,
    color: "green",
    ariaLabel: "Workflows Step"
  },
  {
    id: 3,
    title: "Analyze",
    description: "Interact with your data in tables and chat",
    icon: <BarChart2 className="w-6 h-6 text-purple-600" />,
    color: "purple",
    ariaLabel: "Analysis Step"
  },
  {
    id: 4,
    title: "Share",
    description: "Publish your data, or output the processed results to different systems",
    icon: <Share2 className="w-6 h-6 text-amber-600" />,
    color: "amber",
    ariaLabel: "Share Step"
  }
];

const HowItWorks: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);

  // SEO Schema for HowTo
  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to Process Data with Strive",
    "description": "Create powerful data workflows in minutes with our intuitive no-code interface",
    "step": WORKFLOW_STEPS.map((step) => ({
      "@type": "HowToStep",
      "position": step.id,
      "name": step.title,
      "text": step.description
    })),
    "totalTime": "PT5M",
    "estimatedCost": {
      "@type": "MonetaryAmount",
      "currency": "USD",
      "value": "499"
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Start animations when section is in view
            const steps = entry.target.querySelectorAll('[data-step]');
            const progressLine = entry.target.querySelector('.progress-line');
            
            // Immediately show the section
            entry.target.classList.add('opacity-100');
            
            // Animate steps with shorter delays
            steps.forEach((step, index) => {
              setTimeout(() => {
                step.classList.remove('opacity-0', 'transform', '-translate-x-2', 'translate-y-4');
              }, index * 150);
            });

            // Animate progress line immediately
            if (progressLine) {
              requestAnimationFrame(() => {
                if (window.innerWidth >= 768) {
                  (progressLine as HTMLElement).style.width = '100%';
                } else {
                  (progressLine as HTMLElement).style.height = '100%';
                }
              });
            }

            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.07,
        rootMargin: '50px 0px'
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>How It Works - Create Data Workflows in Minutes | Strive</title>
        <meta 
          name="description" 
          content="Learn how to transform your data processing with Strive's intuitive no-code workflow editor. Extract, analyze, and share data in minutes." 
        />
        <meta 
          name="keywords" 
          content="data workflow, document intelligence, data analysis, no-code workflow, data processing" 
        />
        <meta property="og:title" content="How Strive Works - Data Processing Made Simple" />
        <meta 
          property="og:description" 
          content="Create powerful data workflows in minutes. Extract data from documents, process it with no-code tools, analyze results, and share insights." 
        />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify(howToSchema)}
        </script>
      </Helmet>

      <section 
        ref={sectionRef} 
        id="how-it-works" 
        className="py-24 bg-white workflow-steps opacity-0 transition-opacity duration-300"
        aria-labelledby="how-it-works-title"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 
              id="how-it-works-title"
              className="text-3xl sm:text-4xl font-bold mb-4"
            >
              How It Works
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto px-2">
              Create powerful data workflows in minutes with our intuitive no-code interface.
            </p>
          </div>
          
          {/* Mobile View - Vertical Steps */}
          <div className="md:hidden relative max-w-xs mx-auto px-4">
            <div className="absolute left-6 top-0 bottom-0 w-[2px] bg-gray-200 z-0">
              <div className="h-0 w-full bg-gradient-to-b from-blue-500 via-blue-400 to-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)] transition-all [transition-duration:var(--animation-duration)] ease-out progress-line"></div>
            </div>
            <div className="space-y-12 relative z-10">
              <ul 
                className="space-y-12"
                role="list"
                aria-label="Workflow steps"
              >
                {WORKFLOW_STEPS.map((step) => (
                  <li 
                    key={step.id}
                    className="flex items-start group opacity-0 transition-all duration-500 ease-out transform -translate-x-2" 
                    data-step={step.id}
                    role="listitem"
                    aria-label={step.ariaLabel}
                  >
                    <div className="relative mr-6">
                      <div className={`w-12 h-12 rounded-full bg-${step.color}-100 flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg`}>
                        {step.icon}
                      </div>
                      <div className={`absolute top-0 left-0 bg-${step.color}-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center -translate-x-1/2 -translate-y-1/2`}>
                        {step.id}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-gray-900 mb-1">{step.title}</h3>
                      <p className="text-sm text-gray-600">{step.description}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          {/* Desktop View - Horizontal Steps */}
          <div className="hidden md:block relative max-w-5xl mx-auto">
            <div className="absolute left-0 right-0 top-[60px] h-[2px] bg-gray-200 z-0">
              <div className="h-full bg-gradient-to-r from-blue-500 via-blue-400 to-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)] w-0 transition-all [transition-duration:var(--animation-duration)] ease-out progress-line"></div>
            </div>
            <div className="relative z-10">
              <ul 
                className="flex justify-between"
                role="list"
                aria-label="Workflow steps"
              >
                {WORKFLOW_STEPS.map((step) => (
                  <li 
                    key={step.id}
                    className="flex flex-col items-center group opacity-0 transition-all duration-500 ease-out transform translate-y-4" 
                    data-step={step.id}
                    role="listitem"
                    aria-label={step.ariaLabel}
                  >
                    <div className="relative mb-12">
                      <div className={`w-16 h-16 rounded-full bg-${step.color}-100 flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg`}>
                        {React.cloneElement(step.icon, { className: "w-8 h-8" })}
                      </div>
                      <div className={`absolute top-0 left-0 bg-${step.color}-600 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center -translate-x-1/2 -translate-y-1/2`}>
                        {step.id}
                      </div>
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 mb-1 text-center">{step.title}</h3>
                    <p className="text-sm text-gray-600 text-center max-w-[150px]">{step.description}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HowItWorks; 